import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import {
  ArrowRight,
  ArrowUpRight,
  Check,
  CreditCard,
  MoonStar,
  Home as HomeIcon,
  Footprints,
  DoorOpen,
  PawPrint,
} from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import MaskedLines from "../components/MaskedLines";
import Reveal from "../components/Reveal";
import PawTrail from "../components/PawTrail";
import AriAvatar from "../components/AriAvatar";
import ReviewSection from "../components/ReviewSection";
import MeetAri from "../components/MeetAri";
import GallerySection from "../components/GallerySection";
import api from "../lib/api";

const HERO_IMG =
  "https://images.unsplash.com/photo-1763718170991-baa67106743b?q=85&w=1600&auto=format&fit=crop";

const SERVICE_ICONS = {
  "overnight-house-sitting": MoonStar,
  boarding: HomeIcon,
  "dog-walking": Footprints,
  "drop-in-visits": DoorOpen,
};

const TICKER = [
  "Overnight house sitting",
  "Boarding",
  "Dog walking",
  "Drop-in visits",
  "One sitter, total focus",
  "Free meet & greet",
];

const STEPS = [
  {
    n: "01",
    title: "Tell Ari about your pet",
    copy: "Pick a service, choose your dates and share routines, medication and quirks — the good stuff.",
  },
  {
    n: "02",
    title: "Ari confirms personally",
    copy: "Every request is personally reviewed by Ari, so her yes means your spot is truly saved. You'll hear back within a few hours.",
  },
  {
    n: "03",
    title: "Relax — updates included",
    copy: "A photo and a note after every visit. Come home to a happy pet and a calm house.",
  },
];

const RotatingBadge = () => (
  <Link
    to="/book"
    data-testid="hero-rotating-badge"
    className="group absolute -bottom-8 -left-8 z-10 hidden h-32 w-32 md:block"
    aria-label="Book a sitter"
  >
    <svg viewBox="0 0 100 100" className="h-full w-full animate-spin-slow text-ink">
      <defs>
        <path id="badge-circle" d="M50,50 m-38,0 a38,38 0 1,1 76,0 a38,38 0 1,1 -76,0" fill="none" />
      </defs>
      <text fill="currentColor" style={{ fontFamily: "'Space Mono', monospace", fontSize: "9.5px", letterSpacing: "2.6px" }}>
        <textPath href="#badge-circle">BOOK A SITTER • BOOK A SITTER •</textPath>
      </text>
    </svg>
    <span className="absolute inset-0 flex items-center justify-center">
      <PawPrint className="h-6 w-6 text-marmalade transition-transform duration-300 group-hover:scale-125" />
    </span>
  </Link>
);

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const yText = useTransform(scrollYProgress, [0, 1], [0, 50]);

  return (
    <section ref={ref} className="relative overflow-hidden pb-20 pt-32 md:pb-28 md:pt-40 lg:min-h-[92vh]">
      <div className="pointer-events-none absolute -top-40 right-[-10%] h-[560px] w-[560px] rounded-full bg-marmalade/10 blur-3xl" />
      <div className="pointer-events-none absolute bottom-4 left-0 right-0 hidden justify-center md:flex">
        <PawTrail count={9} className="w-full max-w-4xl px-16" opacityClass="opacity-[0.08]" />
      </div>
      <div className="mx-auto grid max-w-[1440px] grid-cols-1 items-center gap-16 px-6 md:px-12 lg:grid-cols-12 lg:gap-8">
        <motion.div style={{ y: yText }} className="lg:col-span-7">
          <AriAvatar />
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="font-mono text-[11px] uppercase tracking-[0.3em] text-marmalade"
          >
            Pet sitting · done properly
          </motion.p>
          <h1 className="mt-6 font-display text-[clamp(3.2rem,7.5vw,6.8rem)] font-medium leading-[0.95] tracking-tight text-ink">
            <MaskedLines
              delay={0.25}
              lines={[
                <span key="l1">Your pets stay</span>,
                <span key="l2">
                  <em className="text-marmalade">home.</em> Ari stays
                </span>,
                <span key="l3">close by.</span>,
              ]}
            />
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.85 }}
            className="mt-8 max-w-lg text-base leading-relaxed text-inkSoft md:text-lg"
          >
            Overnight house sitting, boarding, walks and drop-in visits — handled personally by Ari, your local
            sitter who sends a photo update after every single visit.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <Link
              to="/book"
              data-testid="hero-book-cta"
              className="group inline-flex items-center gap-3 rounded-full bg-marmalade px-8 py-4 font-mono text-xs uppercase tracking-[0.22em] text-white transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-marmalade-deep"
            >
              Book a sitter
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <button
              onClick={() => document.querySelector("#services")?.scrollIntoView({ behavior: "smooth" })}
              data-testid="hero-services-cta"
              className="inline-flex items-center gap-2 rounded-full border border-ink/15 px-8 py-4 font-mono text-xs uppercase tracking-[0.22em] text-ink transition-[background-color] duration-300 hover:bg-sand"
            >
              Explore services
            </button>
            <Link
              to="/pay"
              data-testid="hero-pay-cta"
              className="pay-pulse inline-flex items-center gap-2 rounded-full border border-ink/15 px-8 py-4 font-mono text-xs uppercase tracking-[0.22em] text-ink transition-[background-color] duration-300 hover:bg-sand"
            >
              <CreditCard className="h-4 w-4 text-marmalade" />
              Pay for a booking
            </Link>
          </motion.div>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1.2 }}
            className="mt-10 flex flex-wrap gap-x-8 gap-y-3"
          >
            {["Insured & background-checked", "Photo updates every visit", "Free meet & greet"].map((t) => (
              <span key={t} className="flex items-center gap-2 text-sm text-inkSoft">
                <Check className="h-4 w-4 text-forest" />
                {t}
              </span>
            ))}
          </motion.div>
        </motion.div>

        <motion.div style={{ y: yImg }} className="relative lg:col-span-5">
          <motion.div
            initial={{ clipPath: "inset(100% 0% 0% 0%)", opacity: 0.4 }}
            animate={{ clipPath: "inset(0% 0% 0% 0%)", opacity: 1 }}
            transition={{ duration: 1.3, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="relative mx-auto max-w-[460px]"
          >
            <div
              className="absolute -right-5 top-10 h-full w-full rounded-[999px_999px_28px_28px] bg-sand"
              aria-hidden="true"
            />
            <div className="relative overflow-hidden rounded-[999px_999px_28px_28px] shadow-[0_30px_60px_-20px_rgba(42,42,42,0.25)]">
              <img
                src={HERO_IMG}
                alt="Golden retriever in warm evening light"
                data-testid="hero-image"
                className="h-[420px] w-full object-cover md:h-[560px]"
              />
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_15%,rgba(217,108,74,0.16),transparent_55%)]" />
            </div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.4, duration: 0.7 }}
              className="absolute -left-6 top-16 hidden items-center gap-3 rounded-2xl border border-line bg-white/90 p-4 shadow-[0_12px_30px_rgba(42,42,42,0.1)] backdrop-blur-md md:flex"
              data-testid="hero-update-card"
            >
              <span className="flex h-10 w-10 items-center justify-center rounded-full bg-forest/10">
                <PawPrint className="h-5 w-5 text-forest" />
              </span>
              <span>
                <span className="block font-mono text-[10px] uppercase tracking-[0.2em] text-inkSoft">
                  Photo update · 7:42 pm
                </span>
                <span className="block text-sm font-semibold text-ink">Maple says hi</span>
              </span>
            </motion.div>
            <RotatingBadge />
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

const Ticker = () => (
  <div className="border-y border-line bg-sand/70" data-testid="services-marquee">
    <Marquee speed={24} gradient={false}>
      <div className="flex items-center">
        {TICKER.map((item) => (
          <span key={item} className="flex items-center gap-6 px-6 py-5 md:py-6">
            <span className="whitespace-nowrap font-display text-2xl italic text-ink md:text-3xl">{item}</span>
            <PawPrint className="h-5 w-5 shrink-0 text-marmalade" />
          </span>
        ))}
      </div>
    </Marquee>
  </div>
);

const ServiceCard = ({ service }) => {
  const Icon = SERVICE_ICONS[service.slug] || PawPrint;
  const r = service.rates || {};
  const ratesLines =
    service.slug === "dog-walking"
      ? [`30 min $${r.walk_30} · 60 min $${r.walk_60}`, `+$${r.dog_additional} per extra dog`]
      : [
          `Dogs $${r.dog}${service.overnight ? "/night" : ""}${r.dog_additional ? ` · +$${r.dog_additional} extra dog` : ""}`,
          `Cats $${r.cat}${service.overnight ? "/night" : ""}${r.cat_additional ? ` · +$${r.cat_additional} extra cat` : ""}`,
        ];
  return (
    <div
      data-testid={`service-card-${service.slug}`}
      className="group flex h-full flex-col overflow-hidden rounded-[2rem] border border-line bg-white shadow-[0_8px_30px_rgba(42,42,42,0.04)] transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-[0_18px_40px_rgba(42,42,42,0.08)]"
    >
      <div className="relative h-52 overflow-hidden md:h-60">
        <img
          src={service.image}
          alt={service.name}
          className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
        />
        <span className="absolute left-5 top-5 flex h-11 w-11 items-center justify-center rounded-full bg-cream/90 backdrop-blur">
          <Icon className="h-5 w-5 text-forest" />
        </span>
        <span
          className="absolute right-5 top-5 rounded-full bg-cream/90 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.18em] text-ink backdrop-blur"
          data-testid={`service-price-${service.slug}`}
        >
          {service.price}
        </span>
      </div>
      <div className="flex flex-1 flex-col p-7 md:p-8">
        <h3 className="font-display text-3xl font-medium text-ink">{service.name}</h3>
        <p className="mt-2 font-display text-lg italic text-inkSoft">{service.tagline}</p>
        <ul className="mt-5 grid grid-cols-1 gap-2 sm:grid-cols-2">
          {service.features.slice(0, 4).map((f) => (
            <li key={f} className="flex items-start gap-2 text-sm text-ink">
              <Check className="mt-0.5 h-4 w-4 shrink-0 text-marmalade" />
              {f}
            </li>
          ))}
        </ul>
        <div className="mt-4 space-y-1.5 rounded-2xl bg-sand/70 px-4 py-3" data-testid={`service-rates-${service.slug}`}>
          {ratesLines.map((line) => (
            <p key={line} className="font-mono text-[10px] uppercase tracking-[0.14em] text-inkSoft">
              {line}
            </p>
          ))}
        </div>
        <div className="mt-auto flex items-center justify-between gap-4 pt-8">
          <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-inkSoft">{service.duration}</span>
          <Link
            to={`/book?service=${service.slug}`}
            data-testid={`service-book-btn-${service.slug}`}
            className="group/btn inline-flex items-center gap-2 rounded-full bg-forest px-6 py-3 font-mono text-[11px] uppercase tracking-[0.2em] text-cream transition-[background-color] duration-300 hover:bg-ink"
          >
            Book
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover/btn:-translate-y-0.5 group-hover/btn:translate-x-0.5" />
          </Link>
        </div>
      </div>
    </div>
  );
};

const Services = () => {
  const [services, setServices] = useState(null);
  const spans = ["lg:col-span-7", "lg:col-span-5", "lg:col-span-5", "lg:col-span-7"];

  useEffect(() => {
    api
      .get("/services")
      .then((r) => setServices(r.data))
      .catch(() => setServices([]));
  }, []);

  return (
    <section id="services" className="mx-auto max-w-[1440px] px-6 py-24 md:px-12 md:py-32">
      <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
        <Reveal>
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-marmalade">What we do</p>
          <h2 className="mt-4 max-w-xl font-display text-4xl font-medium leading-[1.02] tracking-tight text-ink md:text-6xl">
            Care for every <em className="text-marmalade">kind</em> of stay
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="max-w-xs text-sm leading-relaxed text-inkSoft md:text-base">
            Four ways to keep tails wagging — mix and match for your next trip or a busy week.
          </p>
        </Reveal>
      </div>
      <div className="mt-14 grid grid-cols-1 gap-6 md:mt-20 lg:grid-cols-12 lg:gap-8">
        {services === null && <p className="py-16 text-center text-inkSoft">Fetching our services…</p>}
        {services?.length === 0 && (
          <p className="py-16 text-center text-inkSoft">Services are coming right up — check back soon.</p>
        )}
        {services?.map((s, i) => (
          <Reveal key={s.slug} delay={(i % 2) * 0.08} className={spans[i % 4]}>
            <ServiceCard service={s} />
          </Reveal>
        ))}
      </div>
    </section>
  );
};

const HowItWorks = () => (
  <section id="how" className="bg-forest py-24 md:py-32">
    <div className="mx-auto max-w-[1440px] px-6 md:px-12">
      <Reveal>
        <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-cream/60">How it works</p>
        <h2 className="mt-4 max-w-2xl font-display text-4xl font-medium leading-[1.02] tracking-tight text-cream md:text-6xl">
          Requested, <em className="text-[#E8A086]">approved</em>, off you go
        </h2>
      </Reveal>
      <div className="mt-14 grid grid-cols-1 gap-10 md:mt-20 md:grid-cols-3 md:gap-8">
        {STEPS.map((s, i) => (
          <Reveal key={s.n} delay={i * 0.1}>
            <div className="border-t border-cream/25 pt-8">
              <span className="font-mono text-sm text-[#E8A086]">{s.n}</span>
              <h3 className="mt-4 font-display text-2xl font-medium text-cream md:text-3xl">{s.title}</h3>
              <p className="mt-3 text-sm leading-relaxed text-cream/70 md:text-base">{s.copy}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const CtaBand = () => (
  <section className="mx-auto max-w-[1440px] px-6 pb-24 md:px-12 md:pb-32">
    <Reveal>
      <div className="relative overflow-hidden rounded-[2.5rem] bg-ink px-8 py-20 text-center md:py-28">
        <PawPrint className="absolute left-8 top-8 h-10 w-10 text-cream/10" />
        <PawPrint className="absolute bottom-8 right-8 h-14 w-14 text-cream/10" />
        <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#E8A086]">Ready when you are</p>
        <h2 className="mx-auto mt-6 max-w-3xl font-display text-5xl font-medium leading-[0.98] tracking-tight text-cream md:text-7xl">
          The porch light <em className="text-marmalade">is on.</em>
        </h2>
        <p className="mx-auto mt-6 max-w-md text-base leading-relaxed text-cream/70">
          Send a request today — Ari will confirm within a few hours.
        </p>
        <Link
          to="/book"
          data-testid="home-final-book-cta"
          className="mt-10 inline-flex items-center gap-3 rounded-full bg-marmalade px-9 py-4 font-mono text-xs uppercase tracking-[0.22em] text-white transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-marmalade-deep"
        >
          Book your sit
          <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    </Reveal>
  </section>
);

const Home = () => (
  <div className="bg-cream">
    <Navbar />
    <main>
      <Hero />
      <Ticker />
      <Services />
      <HowItWorks />
      <MeetAri />
      <ReviewSection />
      <GallerySection />
      <CtaBand />
    </main>
    <Footer />
  </div>
);

export default Home;
